import { useEffect, useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, TextField } from "@mui/material";
import { getData, postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { ProjectOptions } from "../../types/projectTypes";

type AddProjectProps = {
    onClose: () => void;
    onCreated: (id: number) => void;
};

// Minimal "new project" form - everything else is filled in afterwards in the detail drawer.
function AddProject({ onClose, onCreated }: AddProjectProps) {
    const notify = useNotification();
    const [options, setOptions] = useState<ProjectOptions | null>(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ project_number: '', project_name: '', client_ID: '' as number | '', status: 'Yet to start', lead: true });

    useEffect(() => {
        getData('projects/options').then((res) => setOptions(res as ProjectOptions)).catch((error) => console.error(error));
    }, []);

    const handleSave = async () => {
        if (!form.project_number.trim() || !form.project_name.trim()) {
            notify('Project number and name are required.', 'warning');
            return;
        }
        setSaving(true);
        try {
            const result = await postData('projects/add', {
                project_number: form.project_number.trim(),
                project_name: form.project_name.trim(),
                client_ID: form.client_ID === '' ? null : form.client_ID,
                status: form.status,
                lead: form.lead,
            });
            notify(`Project ${form.project_number} created.`, 'success');
            onCreated(result.id);
        } catch (error) {
            notify(`Could not create project: ${(error as Error).message}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <DialogTitle>New Project</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth required label="INT #" value={form.project_number}
                            onChange={(e) => setForm({ ...form, project_number: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField fullWidth required label="Project name" value={form.project_name}
                            onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Client</InputLabel>
                            <Select label="Client" value={form.client_ID} onChange={(e) => setForm({ ...form, client_ID: e.target.value as number | '' })}>
                                <MenuItem value="">-</MenuItem>
                                {options?.clients.map((client) => <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                {(options?.statuses || ['Yet to start']).map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Switch checked={form.lead} onChange={(e) => setForm({ ...form, lead: e.target.checked })} />}
                            label={form.lead ? 'Integration leads the consortium' : 'Integration is a partner'} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>Create</Button>
            </DialogActions>
        </>
    );
}

export default AddProject;
